/** @jsx jsx */
import type { PropsWithChildren } from 'react';
import { memo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl-next';
import { css, jsx } from '@compiled/react';

import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { IconButton } from '@atlaskit/button/new';
import type { OnOpenChangeArgs } from '@atlaskit/dropdown-menu';
import DropdownMenu from '@atlaskit/dropdown-menu';

const NOOP = () => {};

const i18n = defineMessages({
	triggerLabel: {
		id: 'content-tools.more.icon.label',
		defaultMessage: 'More actions',
		description: 'A label for the button that opens a menu providing additional actions for a page',
	},
});

const dropdownMenuCollaboratorStyle = css({
	marginLeft: '0px',
	minWidth: '24px',
});

const dropdownMenuNonCollaboratorStyle = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '10px',
	minWidth: '24px',
});

const ContentToolsButtonComponent = ({
	isDisabled,
	isOpen = false,
	onOpenChange = NOOP,
	onPositioned = NOOP,
	children,
	isExternalCollaborator = false,
}: ContentToolsButtonProps) => {
	const intl = useIntl();

	const [drodownMenuMaxHeight, setDropdownMenuMaxHeight] = useState('auto');
	const dropdownMenuContainerRef = useRef<HTMLDivElement>(null);

	const handleOnOpenChange: typeof onOpenChange = (obj) => {
		onOpenChange(obj);

		if (obj.isOpen) {
			const distanceFromTop = dropdownMenuContainerRef.current?.getBoundingClientRect().bottom ?? 0;
			const availableHeight = window.innerHeight - distanceFromTop - 16;

			setDropdownMenuMaxHeight(`${availableHeight}px`);
		}
	};

	return (
		<div
			data-id="content-tools-dropdown-menu-container"
			data-test-id="content-tools-dropdown-menu-container"
			data-testid="content-tools-dropdown-menu-container"
			css={[
				dropdownMenuNonCollaboratorStyle,
				isExternalCollaborator && dropdownMenuCollaboratorStyle,
			]}
			ref={dropdownMenuContainerRef}
		>
			<DropdownMenu
				testId="content-tools-dropdown-menu"
				appearance="tall"
				trigger={({ triggerRef, ...props }) => (
					<IconButton
						{...props}
						data-id="page-more-action-button"
						data-test-id="page-more-action-button"
						id="tools-menu-trigger"
						label={intl.formatMessage(i18n.triggerLabel)}
						icon={ShowMoreHorizontalIcon}
						ref={triggerRef}
						appearance="subtle"
						isDisabled={isDisabled}
					/>
				)}
				placement="bottom-end"
				isOpen={isOpen}
				onOpenChange={handleOnOpenChange}
				shouldFlip={false}
				// @ts-ignore `DropdownMenu` removed support for `onPositioned` in v11.
				onPositioned={onPositioned}
				shouldRenderToParent
			>
				<div style={{ maxHeight: drodownMenuMaxHeight }}>{children || null}</div>
			</DropdownMenu>
		</div>
	);
};

ContentToolsButtonComponent.propTypes = {
	children: PropTypes.node,
	isDisabled: PropTypes.bool,
	isExternalCollaborator: PropTypes.bool,
	isOpen: PropTypes.bool,
	onOpenChange: PropTypes.func,
	onPositioned: PropTypes.func,
};

export type ContentToolsButtonProps = PropsWithChildren<{
	isDisabled?: boolean;
	isExternalCollaborator?: boolean;
	isOpen?: boolean;
	onOpenChange?: (args: Pick<OnOpenChangeArgs, 'isOpen'>) => void;
	/**
	 * @deprecated `DropdownMenu` removed support for `onPositioned` in v11.
	 */
	onPositioned?: () => void;
}>;

export const ContentToolsButton = memo(ContentToolsButtonComponent);
